import { colorToCss } from "@omnia/shared/color/color"
import { isFieldViewModel } from "@omnia/shared/fields"
import { watchFields } from "@omnia/shared/fields/watch"
import { DatalayerZone } from "@omnia/shared/layers/datalayerZone"
import { LayerViewModel } from "@omnia/shared/layers/layerViewModel"
import { setBaseLayerStyle } from "@omnia/shared/nutrition/map/baseLayerStyle"
import { defineComponent, FieldViewModel, IDatalayerMapDependencies, INumericValueColor, IOmniaShell, isArrayEmpty, shell } from "../../omnia"
import { NutrientFilter } from "../../services/nutrientFilter"
import { IFieldsService, INutrient, INutrientGroup, INutritionPlan, INutritionService } from "../../types"
import { MapDashboardContext } from "./context"
import { MapContext } from "./map-context"
import { MapFilter } from "./map-filter"
import { MapSelectInteraction } from "./map-select"
import { PlanReader } from "./plan-reader"
import { DashboardSelection } from "./selection"
import { IDashboardContext, IDashboardFilters } from "./types"
import { NtrDashboardMap } from "./view/map"
import { PlanMapView } from "./view/plan"
import { ol_Feature } from "@omnia/exports/openlayers"

export class MapCtrl extends shell.Component {

    static create(): ng.IDirective {
        return defineComponent(MapCtrl, "app/nutrition/dashboard/map/map.html");
    }

    public map: NtrDashboardMap;
    private mapFilter: MapFilter;

    public context: IDashboardContext;

    public mapCtxt: MapContext;

    private selectionWatcher: Function;

    public allCropping: FieldViewModel[];
    private _cropping: FieldViewModel;
    private _disabledCropping: FieldViewModel[];

    private _nutrients: NutrientFilter;

    static $inject = [
        "$scope",
        "OmniaShell",
        "OmniaNutritionService",
        "OmniaFieldsService",
        "OmniaDatalayersDependencies"
    ];

    constructor(
        $scope: ng.IScope,
        private readonly shell: IOmniaShell,
        private readonly svc: INutritionService,
        private readonly fieldsSvc: IFieldsService,
        private readonly mapDependencies: IDatalayerMapDependencies) {

        super($scope);


        this._icon = "icon-nutrition"
        this._title = "Nutrition plans"

    }

    $onInit() {

        this.$scope.$watch("vm.map.visibleLabels", () => this.refreshLabels())

        this.setupMap()

        this.refresh()
            .then(() => watchFields(this.fieldsSvc, this.$scope, () => this.refreshFields()))
            .then(() => {

                this.$scope.$watchGroup(["vm.context.selection", "vm.context.nutrients.nutrient"], () => this.selectViewFromSelection())
                this.$scope.$watch("vm.filters", () => this.applyFilters())
                this.$scope.$watch("vm.context.settings", () => this.applySettings(), true)

                this.$scope.$watch("vm.svc.stateVersion", () => this.refresh())

                this.context.startInitialComponent()

            })

    }

    private refresh() {

        let selection = (this.context ? this.context.selection : null);
        selection = selection ? selection : new DashboardSelection(null);

        return this.svc.refresh().then(() => {

            let reader = new PlanReader(this.svc, this.fieldsSvc);

            if (this.mapCtxt) {
                this.mapCtxt.dispose();
            }

            this.mapCtxt = new MapContext(this.svc, this.fieldsSvc, reader, this.map, this.mapDependencies);
            this.mapCtxt.setup();

            this.setupCrops();

            this.setupNutrients();

            this.context = new MapDashboardContext(this.shell, this.svc, this.fieldsSvc, {

                mapCtxt: this.mapCtxt,
                mapFilter: this.mapFilter,

                nutrients: this._nutrients,

                planReader: reader,
                selection: selection,

                refresh: () => this.refresh(),

            });

        });


    }

    private refreshLabels() {
        if (this.mapCtxt) {
            this.mapCtxt.refreshLabels()
        }
    }

    private setupNutrients() {

        this._nutrients = new NutrientFilter(this.shell.getSession(), this.svc.settings, value => {

            if (value) {
                this.mapCtxt.nutrient = value.nutrient
                this.resetCurrentSelection()
            }

        });

    }

    private setupCrops() {

        let cropping = this.fieldsSvc.cropping.filter(c => c.currentYear);

        this.allCropping = cropping;

    }

    private setupMap() {

        if (this.selectionWatcher) {
            this.selectionWatcher();
            this.selectionWatcher = null;
        }

        if (!this.map) {

            this.mapFilter = new MapFilter(
                this.fieldsSvc.cropping,
                [() => this._disabledCropping],
                () => {
                    if (this.view) return this.view.getLayers();
                    return [];
                });

            const map = this.map = new NtrDashboardMap(
                () => {
                    return this.context;
                },
                this.mapDependencies,
                {
                    selecInteractionFactory: m => new MapSelectInteraction(m, f => this.mapFilter.filterFeatureSelection(f))
                }
            );

            map.croppingLayer.showSelectedFillColor = false
            map.croppingLayer.showSelectedText = false
            map.croppingLayer.showLabels = false

            map.fieldsLayer.showLabels = false

            setBaseLayerStyle(map.croppingLayer)
            setBaseLayerStyle(map.fieldsLayer)

        }

        this.selectionWatcher = this.map.watchSelection(features => this.updateSelection(features));

    }

    get selection() {
        if (this.context) {
            return this.context.selection;
        }
    }

    setSelection(value: DashboardSelection, context: string) {
        this.context.setSelection(value, "MapCtrl/" + context);
    }

    get cropping(): FieldViewModel {
        return this._cropping;
    }

    set cropping(value: FieldViewModel) {
        this._cropping = value;
        let croppingId = value ? value.croppingId : null;
        this.selectCropping(croppingId);
        this.syncSelection(croppingId);
    }

    get nutrients() {
        if (this._nutrients) {
            return this._nutrients.nutrients;
        }
    }

    get nutrient() {
        if (this._nutrients) {
            return this._nutrients.nutrient;
        }
    }

    set nutrient(value: INutrient) {
        this._nutrients.nutrient = value;
    }

    get nutrientGroup() {
        if (this._nutrients) {
            return this._nutrients.nutrientGroup;
        }
    }

    set nutrientGroup(value: INutrientGroup) {
        this._nutrients.nutrientGroup = value;
    }

    get nutrientGroups() {
        if (this._nutrients) {
            return this._nutrients.nutrientGroups;
        }
    }

    get filters(): IDashboardFilters {
        if (this.context) {
            return this.context.filters;
        }
    }

    set filters(value: IDashboardFilters) {
        this.context.filters = value;
    }

    get view() {
        if (this.mapCtxt) {
            return this.mapCtxt.view;
        }
    }

    get showingApplications(): boolean {
        if (this.view instanceof PlanMapView) {
            return true;
        }
    }

    get key() {
        if (this.mapCtxt) {
            return this.mapCtxt.colorKey;
        }
    }

    getKeyCellStyle(c: INumericValueColor) {

        return {
            'background-color': colorToCss(c.Colour)
        }

    }

    getKeyItemValue(c: INumericValueColor) {
        return c.Value;
    }


    private selectViewFromSelection() {

        if (this.mapCtxt) {
            this.mapCtxt.selectViewFromSelection(this.nutrient, this.selection);
        }

    }

    public selectNutrient(value: string | INutrient) {
        this._nutrients.setNutrient(value);
    }

    private updateSelection(features: ol_Feature[]) {

        let croppingId: number;
        let zoneNumber: number;

        if (isArrayEmpty(features)) {

            const s = this.selection

            if (s && s.croppingMode && s.croppingId) {
                croppingId = s.croppingId
            }

        }
        else {

            let feature = features[0];
            let data = <DatalayerZone>feature.get("data");

            let layer = data.layer;

            if (layer && layer.croppingId) {

                croppingId = <number>layer.croppingId;
                zoneNumber = <number>data.zoneNumber;

            }

            else if (isFieldViewModel(data)) {

                croppingId = data.croppingId;

            }

        }

        this.selectCropping(croppingId, zoneNumber)

    }

    public resetCurrentSelection() {

        if (!this.selection) return;

        let croppingId = this.selection.croppingId;

        this.selectCropping(croppingId);

        this.syncSelection(croppingId);


    }

    private syncSelection(croppingId: number) {

        let selection = this.map.getSelectedFeatures();
        let current = selection && (selection.length > 0) ? selection[0] : null;

        let feature: ol_Feature;

        if (this.view) {
            feature = this.view.findZone(croppingId);
        }

        if (croppingId && (!feature)) {
            let cropping = this.fieldsSvc.findCropping(croppingId);
            feature = this.map.findFeatureWithData(cropping);
        }

        if (feature) {
            if (feature != current) {
                this.map.selectFeatures([feature]);
            }
        }
        else if (!!current) {
            this.map.clearSelection();
        }

        this._cropping = croppingId ? this.allCropping.find(c => c.croppingId == croppingId) : null;

    }

    private selectCropping(croppingId: number, zoneNumber?: number) {

        let s = this.selection.focusCropping(croppingId, zoneNumber)
        this.setSelection(s, "selectCropping");

    }


    private applyFilters() {

        if (!this.filters) return;

        let cropping = this.filters.cropping;

        this.applyFiltersToViews(cropping);
        this.applyFiltersToBaseFeatures(cropping);

    }

    private refreshFields() {

        if (this.filters) {
            this.filters = this.filters.refresh()
        }

    }

    private applyFiltersToViews(cropping: FieldViewModel[]) {

        let filter = (lvm: LayerViewModel) => {
            return (!cropping) || cropping.some(c => c.croppingId == lvm.croppingId);
        };

        this.mapCtxt.setVisibilityFilter(filter);

    }

    private applyFiltersToBaseFeatures(cropping: FieldViewModel[]) {

        let hiddenCropping = [];

        if (this.filters && this.filters.crop) {
            hiddenCropping = this.fieldsSvc.cropping.filter(c => cropping.every(i => i != c));
        }

        this._disabledCropping = hiddenCropping;

        this.mapFilter.update();

    }

    private applySettings() {

        if (this.context && this.map) {
            this.mapCtxt.applySettings(this.context.settings);
        }

    }

    private calculateRequirements(croppingIds: number[]): ng.IPromise<boolean> {
        return this.context.calculateRequirements(croppingIds);
    }

    private editRequirements(cropping: FieldViewModel[]) {
        return this.context.editRequirements(cropping);
    }

    private createPlan(croppingsIds: number[]) {
        return this.context.createPlan(croppingsIds);
    }

    private editPlan(plan: INutritionPlan) {
        return this.context.updatePlan(plan);
    }

}
